// Mistake drills (PracticePage.js with ?drill=revenge…): a practice day built around one habit
// the journal keeps catching. The practice account coaches it live (a countdown after a loss, a
// warning before a bigger size, "the day is over" after 2 losses, a trade count) and grades it
// with the exam's checks (exam.js) or its own `broke` count. A drill passes when the rule was kept
// and the day tested it.
import { detectBehavior } from '../behaviorDetect';
import { netPnl } from '../pnl';
import { EXAM_RULES, gradeExamDay } from './exam';

export const DRILLS = {
  revenge: {
    check: 'revenge', from: ['revenge'],
    title: { en: 'Wait after a loss', es: 'Espera después de una pérdida' },
    rule: { en: 'After a losing trade, wait 5 minutes (replay time) before the next one.', es: 'Después de una operación perdedora, espera 5 minutos (tiempo de repetición) antes de la siguiente.' },
    tested: (trades) => trades.some((t) => t.net < 0),
  },
  sizeUp: {
    check: 'sizeUp', from: ['sizeUp'],
    title: { en: 'Same size after a loss', es: 'El mismo tamaño después de una pérdida' },
    rule: { en: 'After a losing trade, the next one is the same size or smaller.', es: 'Después de una operación perdedora, la siguiente es del mismo tamaño o menor.' },
    tested: (trades) => trades.some((t, i) => t.net < 0 && i < trades.length - 1),
  },
  dailyStop: {
    check: 'dailyStop', from: ['streak', 'pastLimit', 'giveBack'],
    title: { en: 'Stop after 2 losses', es: 'Parar después de 2 pérdidas' },
    rule: { en: 'After 2 losing trades, the day is over.', es: 'Después de 2 operaciones perdedoras, el día terminó.' },
    tested: (trades) => trades.filter((t) => t.net < 0).length >= EXAM_RULES.maxLosses,
  },
  overtrading: {
    check: 'overtrading', from: ['overtrading'],
    title: { en: 'A few good trades', es: 'Pocas operaciones buenas' },
    rule: { en: 'No more than 6 trades in the day. Wait for your setups.', es: 'No más de 6 operaciones en el día. Espera tus setups.' },
    tested: (trades) => trades.length >= 1,
  },
  holdTarget: {
    from: ['cutWinners'],
    title: { en: 'Let the trade finish', es: 'Deja que la operación termine' },
    rule: { en: 'Every trade has a stop and a 1.5R target and ends at one of them, never closed by hand. Moving the stop to breakeven is fine.', es: 'Cada operación tiene un stop y un objetivo de 1.5R y termina en uno de ellos, nunca cerrada a mano. Mover el stop al punto de equilibrio está bien.' },
    broke: (trades) => trades.filter((t) => byHand(t) || !(rrOf(t) >= EXAM_RULES.minRR)).length,
    tested: (trades) => trades.some((t) => t.exit === 'target' || byHand(t)),
  },
  planFirst: {
    from: ['unplanned'],
    title: { en: 'Plan it before you click', es: 'Planéala antes de hacer clic' },
    rule: { en: 'Every entry has its stop and target in the ticket before you place it, with at least 1.5R to the target.', es: 'Cada entrada tiene su stop y su objetivo en el ticket antes de enviarla, con al menos 1.5R hasta el objetivo.' },
    broke: (trades) => trades.filter((t) => !t.planned || !(rrOf(t) >= EXAM_RULES.minRR)).length,
    tested: (trades) => trades.length >= 1,
  },
  winSizeUp: {
    from: ['winSizeUp'],
    title: { en: 'Same size after a win', es: 'El mismo tamaño después de una ganancia' },
    rule: { en: 'After a winning trade, the next one is the same size or smaller. A win is not a reason to size up.', es: 'Después de una operación ganadora, la siguiente es del mismo tamaño o menor. Ganar no es razón para subir el tamaño.' },
    broke: (trades) => trades.filter((t, i) => i > 0 && trades[i - 1].net > 0 && t.size > trades[i - 1].size).length,
    tested: (trades) => trades.some((t, i) => t.net > 0 && i < trades.length - 1),
  },
  window: {
    from: ['offHours'],
    title: { en: 'Trade your window', es: 'Opera en tu horario' },
    rule: { en: 'Enter trades only between 8:30 and 10:30 a.m. Chicago time, the first two hours. After that, the day is over.', es: 'Entra en operaciones solo entre las 8:30 y las 10:30 a. m. hora de Chicago, las dos primeras horas. Después, el día terminó.' },
    broke: (trades) => trades.filter((t) => !inWindow(chicagoMinutes(t.entryTime))).length,
    tested: (trades) => trades.length >= 1,
  },
};

// Minutes after midnight, Chicago time (CME's clock), of a time in seconds.
export const chicagoMinutes = (t) => {
  const [h, m] = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' })
    .format(new Date(t * 1000)).split(':').map(Number);
  return h * 60 + m;
};
export const WINDOW = [8 * 60 + 30, 10 * 60 + 30];
const inWindow = (minute) => minute >= WINDOW[0] && minute < WINDOW[1];
// Closed by hand (not at its stop or target, and not by the end of the day or the drill).
const byHand = (t) => t.exit === 'flatten' || t.exit === 'market' || t.exit === 'limit' || t.exit === 'stopEntry';
// Reward to risk from the first stop and target the trade had (null without both).
const rrOf = (t) => (Number.isFinite(t.stopPrice) && Number.isFinite(t.targetPrice) && t.entryPrice !== t.stopPrice
  ? Math.abs(t.targetPrice - t.entryPrice) / Math.abs(t.entryPrice - t.stopPrice) : null);
export const DRILL_IDS = Object.keys(DRILLS);
export const drillFor = (kind) => DRILL_IDS.find((id) => DRILLS[id].from.includes(kind)) || null;

// The drill for the habit that cost the most in the last `days` of trades, or null.
export function topDrill(trades, { accounts = [], notes = {}, days = 30, today = new Date() } = {}) {
  if (!trades.length) return null;
  const since = new Date(today.getTime() - days * 86400000);
  const recent = trades.filter((t) => new Date(t.date) >= since);
  const found = detectBehavior(trades, { accounts, notes });
  const cost = {};
  for (const t of recent) {
    for (const f of found.flags.get(t._id) || []) {
      const id = drillFor(f.kind);
      if (id) cost[id] = (cost[id] || 0) + netPnl(t);
    }
  }
  const worst = Object.entries(cost).filter(([, c]) => c < 0).sort((a, b) => a[1] - b[1])[0];
  return worst ? { id: worst[0], cost: worst[1] } : null;
}

// A practice day for a drill: one of the last 20 weekdays (before a week ago), at random.
export function drillDay(today = new Date(), rand = Math.random) {
  const days = [];
  const d = new Date(today);
  d.setDate(d.getDate() - 7);
  while (days.length < 20) {
    if (d.getDay() !== 0 && d.getDay() !== 6) days.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    d.setDate(d.getDate() - 1);
  }
  return days[Math.floor(rand() * days.length)];
}

// The drill's grade: the exam's checks for the day, whether the drill's own rule held, and
// whether the day tested it at all (no loss, no test).
export function gradeDrill(id, trades, opts) {
  const drill = DRILLS[id];
  const grade = gradeExamDay(trades, opts);
  const sorted = trades.slice().sort((a, b) => a.entryTime - b.entryTime);
  const times = drill.broke ? drill.broke(sorted) : 0;
  const focus = drill.check ? grade.checks.find((c) => c.id === drill.check) : { id, ok: times === 0, times };
  const tested = drill.tested(sorted);
  return { ...grade, focus, tested, passed: tested && focus.ok };
}

// While the drill runs: what to show right now. `now` is the replay time (seconds); `ticket` is
// the order ticket's stop and target in ticks; `open` the position ({ inProfit, target }) or null.
export function drillCoach(id, trades, now, nextSize, { ticket = {}, open = null } = {}) {
  const sorted = trades.slice().sort((a, b) => a.exitTime - b.exitTime);
  const last = sorted[sorted.length - 1];
  const losses = trades.filter((t) => t.net < 0).length;
  if (id === 'revenge' && last && last.net < 0 && now - last.exitTime < EXAM_RULES.coolDown) {
    return { kind: 'wait', left: EXAM_RULES.coolDown - (now - last.exitTime) };
  }
  if (id === 'sizeUp' && last && last.net < 0 && nextSize > last.size) return { kind: 'size', last: last.size };
  if (id === 'dailyStop' && losses >= EXAM_RULES.maxLosses) return { kind: 'done', losses };
  if (id === 'overtrading') return { kind: 'count', trades: trades.length, max: EXAM_RULES.maxTrades };
  if (id === 'holdTarget' && open) return open.target === null ? { kind: 'noTarget' } : open.inProfit ? { kind: 'hold', target: open.target } : null;
  if (id === 'planFirst' && !open) {
    if (!(ticket.stopTicks > 0) || !(ticket.targetTicks > 0)) return { kind: 'plan' };
    if (ticket.targetTicks / ticket.stopTicks < EXAM_RULES.minRR) return { kind: 'planRR' };
  }
  if (id === 'winSizeUp' && last && last.net > 0 && nextSize > last.size) return { kind: 'winSize', last: last.size };
  if (id === 'window') {
    const minute = chicagoMinutes(now);
    return minute >= WINDOW[1] ? { kind: 'closed' } : { kind: 'window', left: Math.max(0, (WINDOW[1] - minute) * 60) };
  }
  return null;
}
